/**
 * Run async work over items with a fixed concurrency limit.
 * Preserves input order in the returned array.
 *
 * @template T, R
 * @param {T[]} items
 * @param {number} limit - Max in-flight promises (clamped to >= 1)
 * @param {(item: T, index: number) => Promise<R>} worker
 * @returns {Promise<R[]>}
 */
export async function mapWithConcurrency(items, limit, worker) {
  const list = Array.isArray(items) ? items : [];
  if (!list.length) return [];

  const concurrency = Math.max(1, Math.min(Number(limit) || 1, list.length));
  const results = new Array(list.length);
  let nextIndex = 0;

  const runWorker = async () => {
    while (nextIndex < list.length) {
      const index = nextIndex;
      nextIndex += 1;
      results[index] = await worker(list[index], index);
    }
  };

  await Promise.all(
    Array.from({ length: concurrency }, () => runWorker()),
  );

  return results;
}

/** Default concurrency for bulk document/print fetches (avoids rate limits). */
export const BULK_PRINT_FETCH_CONCURRENCY = 6;

export default mapWithConcurrency;
